import { sanitizeTypedAnswerWithGivenUnit } from "./answerMatchUtils";

export const normalizeQuestionType = (question) =>
  String(question?.type || question?.question_type || "")
    .toLowerCase()
    .replace(/_/g, "-");

export const isFillQuestionType = (typeOrQuestion) => {
  const type =
    typeof typeOrQuestion === "string"
      ? typeOrQuestion.toLowerCase().replace(/_/g, "-")
      : normalizeQuestionType(typeOrQuestion);
  return type === "multi-fill" || type === "fill-in-the-blank" || type === "table";
};

export const parsePipeJoinedBlanks = (text) => {
  if (text == null || text === "") return [];
  if (Array.isArray(text)) return text.map((v) => (v == null ? "" : String(v)));
  return String(text)
    .split(/\s*\|\s*/)
    .map((s) => s.trim());
};

export const getFillTypeMarksTotal = (question) => {
  const blanks = question?.answer_data?.blank_marks ?? question?.answer?.blank_marks;
  const labelCount = (question?.answer_data?.blank_labels ?? question?.answer?.blank_labels ?? []).length;
  const correctBlanks = (question?.answer_data?.correct_blanks ?? question?.answer?.correct_blanks ?? []).length;
  const blankCount = labelCount || correctBlanks;
  if (Array.isArray(blanks) && blanks.length > 0 && (blankCount === 0 || blanks.length === blankCount)) {
    // Per-blank marks are fractional whenever a fixed total is split across the blanks (4 over 8 table
    // cells = 0.5 each). parseInt truncated every one of those to 0, so the whole question fell back to
    // its raw mark and each blank silently re-read as 1.
    const total = Math.round(blanks.reduce((sum, m) => sum + (Number(m) || 0), 0) * 100) / 100;
    if (total > 0) return total;
  }
  return Math.max(Number(question?.marks) || 0, Number(question?.default_marks) || 0) || 0;
};

export const coerceFillAnswersArray = (submitted, correct) => {
  if (Array.isArray(submitted)) return submitted;
  if (typeof submitted === "string" && submitted.includes("|")) {
    return parsePipeJoinedBlanks(submitted);
  }
  const correctLen = Array.isArray(correct) ? correct.length : 0;
  if (typeof submitted === "string" && correctLen > 1) {
    const commaParts = submitted.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    if (commaParts.length > 1 && commaParts.length <= correctLen) return commaParts;
  }
  return submitted == null || submitted === "" ? [] : [String(submitted)];
};

export const isPerBlankFillDisplay = (question, submitted, correct) => {
  if (!isFillQuestionType(question)) return false;
  const type = normalizeQuestionType(question);
  if (type === "table" && question?.answer_data?.table_layout?.grid?.length > 0) return true;
  const sub = coerceFillAnswersArray(submitted, correct);
  const cor = Array.isArray(correct) ? correct : null;
  return Array.isArray(sub) && Array.isArray(cor) && cor.length > 0;
};

const stripHtml = (html) => String(html ?? "").replace(/<[^>]*>/g, "").trim();

export const inferBlankLabelPosition = (label) => {
  const plain = stripHtml(label);
  if (!plain) return "before";
  if (/[□_…]{1,}|\.{3,}/.test(plain) || plain.includes("=")) return "before";
  // Order endpoints: Coldest _____ … _____ Warmest
  if (/\b(warmest|hottest|highest|largest|greatest|last)\b/i.test(plain)) {
    return "after";
  }
  // Descriptor labels that name the blank (e.g. "First Box", "Second answer", "Part a")
  // read naturally before the input: "First Box [____]". Unit labels (onions, cm, g)
  // read after the input: "[____] onions".
  if (/\b(first|second|third|fourth|fifth|sixth|seventh|eighth|box|blank|part|answer|value|column|row|coldest|lowest|smallest|least)\b/i.test(plain)) {
    return "before";
  }
  return "after";
};

export const resolveBlankLabelPosition = (label, positions, idx) => {
  const stored = positions?.[idx];
  if (stored === "after" || stored === "before") return stored;
  return inferBlankLabelPosition(label);
};

export const isFixedFillMarks = (question) => {
  const ad = question?.answer_data || {};
  const ans = question?.answer || {};
  return (ad.marks_mode || ans.marks_mode) === "fixed";
};

export const getFixedFillMarksTotal = (question) => {
  const ad = question?.answer_data || {};
  const ans = question?.answer || {};
  const total = ad.marks_total ?? ans.marks_total ?? question?.marks ?? question?.default_marks;
  return Math.max(0, Number(total) || 0);
};

export const getDisplayFillMarksTotal = (question) => {
  if (isFixedFillMarks(question)) {
    const fixed = getFixedFillMarksTotal(question);
    if (fixed > 0) return fixed;
  }
  return getFillTypeMarksTotal(question);
};

export const getFixedMarksPerBlankLabel = (totalMarks, blankCount) => {
  const n = Math.max(1, blankCount || 1);
  const t = Math.max(0, Number(totalMarks) || 0) || 1;
  return `${Math.round(t * 100) / 100}/${n}`;
};

// A fixed-total fill question splits its mark across the blanks (4 marks over 8 table cells = 0.5 each).
// The split is materialised into blank_marks so every surface that sums per-blank marks agrees with the
// declared total instead of counting 1 per blank; the rounding drift lands on the last blank so the sum
// is exact (5 over 3 → 1.67 / 1.67 / 1.66).
export const distributeFixedBlankMarks = (totalMarks, blankCount) => {
  const n = Math.max(0, Math.trunc(blankCount) || 0);
  const total = Math.max(0, Number(totalMarks) || 0);
  if (n === 0 || total === 0) return [];
  const each = Math.round((total / n) * 100) / 100;
  const arr = Array.from({ length: n }, () => each);
  const drift = Math.round((total - each * n) * 100) / 100;
  if (drift !== 0) arr[n - 1] = Math.round((each + drift) * 100) / 100;
  return arr;
};

/** Per-blank unit chip meta from answer_data.blank_units[i] (server-inferred, non-secret). */
export const getBlankUnitMeta = (question, idx) => {
  const raw = question?.answer_data?.blank_units?.[idx];
  if (!raw || typeof raw !== "object") return { unit: null, prefix: false };
  const unit = String(raw.unit ?? "").trim();
  if (!unit) return { unit: null, prefix: false };
  return { unit, prefix: raw.prefix === true };
};

// One blank can hold a LIST of values ("What are the TWO possible pairs…" → "66, 66"). The unit belongs
// to each of them, so it is joined per value. A comma with exactly three digits after it is a thousands
// separator ("1,200"), not a list.
const BLANK_LIST_SEPARATOR = /,(?!\d{3}(?!\d))/;

const stripOneBlankUnit = (text, unitMeta) => {
  let base = text;
  if (unitMeta.prefix && text.startsWith(unitMeta.unit)) {
    base = text.slice(unitMeta.unit.length);
    base = base.startsWith(" ") ? base.slice(1) : base;
  } else if (!unitMeta.prefix && text.endsWith(unitMeta.unit)) {
    base = text.slice(0, -unitMeta.unit.length);
    base = base.endsWith(" ") ? base.slice(0, -1) : base;
  }
  return sanitizeTypedAnswerWithGivenUnit(base, unitMeta.unit, { isPrefix: !!unitMeta.prefix });
};

const joinOneBlankUnit = (text, unitMeta) => {
  const cleaned = sanitizeTypedAnswerWithGivenUnit(text, unitMeta.unit, { isPrefix: !!unitMeta.prefix });
  if (!cleaned) return "";
  return unitMeta.prefix ? `${unitMeta.unit} ${cleaned}` : `${cleaned} ${unitMeta.unit}`;
};

/** Strip adornment unit for display (mirrors TypeDrawInput). */
export const stripBlankUnitDisplay = (value, unitMeta) => {
  const text = value == null ? "" : String(value);
  if (!unitMeta?.unit) return text;
  const parts = text.split(BLANK_LIST_SEPARATOR);
  if (parts.length < 2) return stripOneBlankUnit(text, unitMeta);
  return parts.map((p) => stripOneBlankUnit(p.trim(), unitMeta)).join(", ");
};

/** Join typed value with adornment unit for storage / grading (mirrors TypeDrawInput). */
export const joinBlankUnitValue = (text, unitMeta) => {
  if (!unitMeta?.unit) return text ?? "";
  if (!text) return "";
  const parts = String(text).split(BLANK_LIST_SEPARATOR);
  if (parts.length < 2) return joinOneBlankUnit(text, unitMeta);
  // An empty part is a comma the student has just typed and not filled in yet ("66, ") — it must
  // survive the round-trip through stripBlankUnitDisplay, or the comma vanishes as it is typed and a
  // second value can never be entered.
  return parts
    .map((p) => p.trim())
    .map((p) => (p === "" ? "" : joinOneBlankUnit(p, unitMeta)))
    .join(", ");
};
